import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import EditIcon from '@mui/icons-material/EditOutlined';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import useVoyagerStore from '../../stores/useVoyagerStore';
import type { WorkspaceAgentTurn, WorkspaceAgentClarifyingQuestion } from '../../types';

type SingleState = {
  mode: 'single';
  selectedIndex: number | null; // options.length === "Something else"
  customText: string;
};

type MultiState = {
  mode: 'multi';
  selected: Set<number>;
};

type QuestionState = SingleState | MultiState;

function initialState(q: WorkspaceAgentClarifyingQuestion): QuestionState {
  const opts = q.options || [];
  if (q.multi_select) {
    return { mode: 'multi', selected: new Set<number>() };
  }
  return { mode: 'single', selectedIndex: opts.length > 0 ? 0 : null, customText: '' };
}

function formatAnswer(q: WorkspaceAgentClarifyingQuestion, st: QuestionState): string {
  const opts = q.options || [];
  if (st.mode === 'multi') {
    const picks = Array.from(st.selected).sort((a, b) => a - b).map((i) => opts[i]).filter(Boolean);
    return picks.join(', ');
  }
  if (st.selectedIndex == null) return '';
  if (st.selectedIndex < opts.length) return opts[st.selectedIndex];
  return st.customText.trim();
}

export default function ClarificationCard({ turn }: { turn: WorkspaceAgentTurn }) {
  const send = useVoyagerStore((s) => s.sendWorkspaceAgentQuery);
  const markSubmitted = useVoyagerStore((s) => s.markWorkspaceAgentClarificationSubmitted);

  const questions: WorkspaceAgentClarifyingQuestion[] =
    turn.response?.clarifying_questions || [];

  const [states, setStates] = React.useState<Record<string, QuestionState>>(() => {
    const init: Record<string, QuestionState> = {};
    for (const q of questions) init[q.id] = initialState(q);
    return init;
  });
  const [pageIdx, setPageIdx] = React.useState(0);
  const customInputRef = React.useRef<HTMLInputElement | null>(null);

  const isActive = !turn.clarificationSubmitted && questions.length > 0;
  const currentQ = questions[pageIdx];
  const isLastPage = pageIdx === questions.length - 1;

  const currentAnswered = currentQ
    ? formatAnswer(currentQ, states[currentQ.id] || initialState(currentQ)).length > 0
    : false;
  const allAnswered = questions.every((q) =>
    formatAnswer(q, states[q.id] || initialState(q)).length > 0
  );

  const setQuestion = (qid: string, patch: Partial<SingleState>) => {
    setStates((prev) => {
      const cur = prev[qid];
      if (!cur || cur.mode !== 'single') return prev;
      return { ...prev, [qid]: { ...cur, ...patch } as SingleState };
    });
  };

  const toggleMulti = (qid: string, idx: number) => {
    setStates((prev) => {
      const cur = prev[qid];
      if (!cur || cur.mode !== 'multi') return prev;
      const next = new Set(cur.selected);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return { ...prev, [qid]: { mode: 'multi', selected: next } };
    });
  };

  const pickSingle = (qid: string, idx: number, commit = false) => {
    const q = questions.find((x) => x.id === qid);
    const opts = q?.options || [];
    const isCustom = idx === opts.length;
    setQuestion(qid, { selectedIndex: idx });
    if (isCustom) {
      setTimeout(() => customInputRef.current?.focus(), 0);
      return;
    }
    if (!commit || qid !== currentQ?.id) return;
    if (isLastPage) {
      // Project the just-clicked value for submit readiness
      const projected = opts[idx] || '';
      const ok = questions.every((qq) => {
        if (qq.id === qid) return projected.length > 0;
        return formatAnswer(qq, states[qq.id] || initialState(qq)).length > 0;
      });
      if (!ok) return;
      const parts = questions.map((qq) => {
        const st = qq.id === qid
          ? ({ mode: 'single', selectedIndex: idx, customText: '' } as SingleState)
          : states[qq.id];
        return `Q: ${qq.prompt}\nA: ${formatAnswer(qq, st)}`;
      });
      submit(parts.join('\n\n'));
    } else {
      setPageIdx((p) => Math.min(questions.length - 1, p + 1));
    }
  };

  const submit = (body: string) => {
    markSubmitted(turn.id);
    send(body);
  };

  const handleSubmit = () => {
    if (!isActive || !allAnswered) return;
    const parts = questions.map((q) => {
      const st = states[q.id] || initialState(q);
      return `Q: ${q.prompt}\nA: ${formatAnswer(q, st)}`;
    });
    submit(parts.join('\n\n'));
  };

  const handleSkip = () => {
    if (!isActive) return;
    submit('Use your best judgment — pick sensible defaults for the questions above and proceed.');
  };

  if (!isActive) {
    // ---- Collapsed / answered state ----
    return (
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
        {questions.map((q) => {
          const answer = formatAnswer(q, states[q.id] || initialState(q));
          return (
            <Box key={q.id} sx={{ '&:not(:last-child)': { mb: 1 } }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 11 }}>
                {q.prompt}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: 13, overflowWrap: 'anywhere' }}>
                {answer || '—'}
              </Typography>
            </Box>
          );
        })}
      </Paper>
    );
  }

  // ---- Active state ----
  const q = currentQ!;
  const st = states[q.id] || initialState(q);
  const opts = q.options || [];

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, gap: 0.5 }}>
        <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 600, lineHeight: 1.3, fontSize: 13 }}>
          {q.prompt}
        </Typography>
        {questions.length > 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <IconButton
              size="small"
              onClick={() => setPageIdx((p) => Math.max(0, p - 1))}
              disabled={pageIdx === 0}
              aria-label="Previous question"
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 32, textAlign: 'center', fontSize: 10 }}>
              {pageIdx + 1}/{questions.length}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setPageIdx((p) => Math.min(questions.length - 1, p + 1))}
              disabled={isLastPage || !currentAnswered}
              aria-label="Next question"
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mb: 1 }}>
        {opts.map((opt, i) => {
          const selected =
            st.mode === 'multi' ? st.selected.has(i) : st.selectedIndex === i;
          return (
            <Box
              key={`${q.id}_${i}`}
              onClick={() => (st.mode === 'multi' ? toggleMulti(q.id, i) : pickSingle(q.id, i, true))}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1,
                py: 0.75,
                borderRadius: 1.5,
                cursor: 'pointer',
                bgcolor: selected ? 'var(--accent-soft)' : 'transparent',
                '&:hover': { bgcolor: selected ? 'var(--accent-tint)' : 'action.hover' },
              }}
            >
              {st.mode === 'multi' ? (
                selected ? (
                  <CheckBoxIcon fontSize="small" color="primary" />
                ) : (
                  <CheckBoxOutlineBlankIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                )
              ) : (
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    bgcolor: 'action.hover',
                    color: 'text.secondary',
                    fontSize: 11,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {i + 1}
                </Box>
              )}
              <Typography variant="body2" sx={{ flex: 1, fontSize: 13, overflowWrap: 'anywhere' }}>
                {opt}
              </Typography>
              {st.mode === 'single' && selected && (
                <KeyboardReturnIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              )}
            </Box>
          );
        })}

        {/* Free-text escape hatch (single-select only) */}
        {st.mode === 'single' && (() => {
          const customIdx = opts.length;
          const isCustom = st.selectedIndex === customIdx;
          return (
            <Box
              onClick={() => pickSingle(q.id, customIdx)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1,
                py: 0.75,
                borderRadius: 1.5,
                cursor: 'pointer',
                bgcolor: isCustom ? 'var(--accent-soft)' : 'transparent',
                '&:hover': { bgcolor: isCustom ? 'var(--accent-tint)' : 'action.hover' },
              }}
            >
              <EditIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              {isCustom ? (
                <TextField
                  inputRef={customInputRef}
                  size="small"
                  variant="standard"
                  fullWidth
                  placeholder="Type your answer…"
                  value={st.customText}
                  onChange={(e) => setQuestion(q.id, { customText: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (!currentAnswered) return;
                      if (isLastPage) handleSubmit();
                      else setPageIdx((p) => Math.min(questions.length - 1, p + 1));
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  InputProps={{ disableUnderline: true, sx: { fontSize: 13 } }}
                />
              ) : (
                <Typography variant="body2" sx={{ flex: 1, fontSize: 13, color: 'text.secondary' }}>
                  Something else
                </Typography>
              )}
            </Box>
          );
        })()}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {isLastPage ? (
          <Button size="small" variant="contained" disabled={!allAnswered} onClick={handleSubmit}>
            Submit
          </Button>
        ) : (
          <Button
            size="small"
            variant="contained"
            disabled={!currentAnswered}
            onClick={() => setPageIdx((p) => Math.min(questions.length - 1, p + 1))}
          >
            Next
          </Button>
        )}
        <Button size="small" variant="text" onClick={handleSkip} sx={{ color: 'text.secondary' }}>
          Skip
        </Button>
      </Box>
    </Paper>
  );
}
